import * as React from "react";

import includes from "lodash/includes";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import type { SemanticCOLORS } from "semantic-ui-react";

import type { CalendarDate } from "../dateAdapter";
import type { RangeIndexes } from "../views/BaseCalendarView";

interface HandleChangeParams {
  value?: string;
  itemPosition?: number;
}

export interface BasePickerOnChangeData {
  [key: string]: any;
  value: {
    /** Year. */
    year?: number;
    /** Month (0 - 11). */
    month?: number;
    /** Date (1 - 31). */
    date?: number;
    /** Hour (0 - 23). */
    hour?: number;
    /** Minute (0 - 59). */
    minute?: number;
    /** Selected start date. */
    start?: CalendarDate;
    /** Selected end date. */
    end?: CalendarDate;
  };
}

export interface BasePickerProps {
  /** Called after day is selected. */
  onChange: (
    e: React.SyntheticEvent<HTMLElement>,
    data: BasePickerOnChangeData,
  ) => void;
  /** Currently selected date. */
  value?: CalendarDate;
  /** A value for initializing day picker's state. */
  initializeWith: CalendarDate;
  /** Forse popup to close. */
  closePopup: () => void;
  /** Whether to display picker without a popup or inside a popup. */
  inline: boolean;
  /** WHether picker in focus. */
  isPickerInFocus: () => boolean;
  /** Whether popup-trigger in focus. */
  isTriggerInFocus: () => boolean;
  /** Used to pass underlying picker's html element to parent component. */
  onCalendarViewMount: (e: HTMLElement) => void;
  /** Called on calendar's header click. */
  onHeaderClick: () => void;
  /** Moment date localization */
  localization?: string;
  tabIndex?: string;
  pickerWidth?: string;
  pickerStyle?: object;
}

export interface OptionalHeaderProps {
  /** Whether to display calendar's header. */
  hasHeader: boolean;
}

export interface DisableValuesProps {
  /** Array of disabled dates. */
  disable?: CalendarDate[];
}

export interface EnableValuesProps {
  /** Array of enabled dates. */
  enable?: CalendarDate[];
}

export interface MinMaxValueProps {
  /** Minimal date that could be selected. */
  minDate?: CalendarDate;
  /** Maximal date that could be selected. */
  maxDate?: CalendarDate;
}

export interface MarkedValuesProps {
  /** Array of marked dates. */
  marked?: CalendarDate[];
  /** String specifying the mark color (Optional). */
  markColor?: SemanticCOLORS;
}

export interface TimePickerProps {
  timeFormat: TimeFormat;
}

export type TimeFormat = "ampm" | "AMPM" | "24";

export interface BasePickerState extends Readonly<any> {
  /** Position of a cell that is currently hovered on. */
  hoveredCellPosition: number | undefined;
  /** Inner picker's currently selected date. */
  date: CalendarDate;
}

/** Do not expose this class. Instead use RangeSelectionPicker and SingleSelectionPicker. */
class BasePicker<P extends BasePickerProps> extends React.Component<
  P,
  BasePickerState
> {
  protected PAGE_WIDTH: number;

  constructor(props: P) {
    super(props);
    this.state = {
      hoveredCellPosition: undefined,
      date: this.props.initializeWith.clone(),
    };
  }

  public componentDidMount(): void {
    document.addEventListener("keydown", this.handleKeyPress);
  }

  public componentWillUnmount(): void {
    document.removeEventListener("keydown", this.handleKeyPress);
  }

  protected onHoveredCellPositionChange = (
    e: React.SyntheticEvent<HTMLElement>,
    { itemPosition }: { itemPosition: number },
  ): void => {
    this.setState({ hoveredCellPosition: itemPosition });
  };

  protected canCalendarCatchKeyboardEvents = (): boolean => {
    if (this.props.inline) {
      return this.props.isPickerInFocus();
    }

    return this.props.isTriggerInFocus();
  };

  protected handleKeyPress = (event: KeyboardEvent): void => {
    if (!this.canCalendarCatchKeyboardEvents()) {
      return;
    }
    const key = event.key;
    switch (key) {
      case "Enter":
        this.handleEnterKeyPress(event);
        break;
      case "Esc":
      case "Escape":
        this.props.closePopup();
        break;
      default:
        this.handleArrowKeyPress(event);
    }
  };

  protected handleEnterKeyPress = (event: KeyboardEvent): void => {
    const key = event.key;
    if (key === "Enter" && this.canCalendarCatchKeyboardEvents()) {
      event.preventDefault();
      const selectedValue =
        this.buildCalendarValues()[this.state.hoveredCellPosition];
      this.handleChange(null, {
        value: selectedValue,
        itemPosition: this.state.hoveredCellPosition,
      });
    }
  };

  protected handleBlur = (): void => {
    this.props.closePopup();
  };

  protected handleArrowKeyPress = (event: KeyboardEvent): void => {
    if (!this.canCalendarCatchKeyboardEvents()) {
      return;
    }
    const key = event.key;
    const selectableCells = this.getSelectableCellPositions();
    const nextSelectableCellPositionLeft = selectableCells
      .slice(0, selectableCells.indexOf(this.state.hoveredCellPosition))
      .pop();
    const nextSelectableCellPositionRight = selectableCells.slice(
      selectableCells.indexOf(this.state.hoveredCellPosition) + 1,
    )[0];
    switch (key) {
      case "Left":
      case "ArrowLeft":
        if (!isNil(nextSelectableCellPositionLeft)) {
          this.onHoveredCellPositionChange(null, {
            itemPosition: nextSelectableCellPositionLeft,
          });
        } else {
          if (this.isPrevPageAvailable()) {
            this.switchToPrevPage(null, null, () => {
              const selectableCellsPrevPage = this.getSelectableCellPositions();
              this.onHoveredCellPositionChange(null, {
                itemPosition:
                  selectableCellsPrevPage[selectableCellsPrevPage.length - 1],
              });
            });
          }
        }
        break;
      case "Right":
      case "ArrowRight":
        if (!isNil(nextSelectableCellPositionRight)) {
          this.onHoveredCellPositionChange(null, {
            itemPosition: nextSelectableCellPositionRight,
          });
        } else {
          if (this.isNextPageAvailable()) {
            this.switchToNextPage(null, null, () => {
              const selectableCellsNextPage = this.getSelectableCellPositions();
              this.onHoveredCellPositionChange(null, {
                itemPosition: selectableCellsNextPage[0],
              });
            });
          }
        }
        break;
      case "Up":
      case "ArrowUp":
        event.preventDefault();
        if (
          includes(
            selectableCells,
            this.state.hoveredCellPosition - this.PAGE_WIDTH,
          )
        ) {
          this.onHoveredCellPositionChange(null, {
            itemPosition: this.state.hoveredCellPosition - this.PAGE_WIDTH,
          });
        }
        break;
      case "Down":
      case "ArrowDown":
        event.preventDefault();
        if (
          includes(
            selectableCells,
            this.state.hoveredCellPosition + this.PAGE_WIDTH,
          )
        ) {
          this.onHoveredCellPositionChange(null, {
            itemPosition: this.state.hoveredCellPosition + this.PAGE_WIDTH,
          });
        }
        break;
      default:
        break;
    }
  };

  /** Return a position of a value (date, year, month ...) with wich a calendar was initialized. */
  getInitialDatePosition(): number {}

  /** Creates values with wich calendar filled. */
  buildCalendarValues(): string[] {}

  /** Handles currently selected value change. */
  handleChange(
    e: React.SyntheticEvent<HTMLElement>,
    data: HandleChangeParams,
  ): void {}

  /** Return positions of all values on calendar that can be selected. */
  getSelectableCellPositions(): number[] {}

  /** Check if calendar has selectable values on previous page (i.e. prev year, month, day). */
  isPrevPageAvailable(): boolean {}

  /** Check if calendar has selectable values on next page (i.e. next year, month, day). */
  isNextPageAvailable(): boolean {}

  /** Change currently displayed page (i.e. year, month, day) to previous one. */
  switchToPrevPage(
    e?: React.SyntheticEvent<HTMLElement>,
    data?: any,
    cb?: () => void,
  ): void {}

  /** Change currently displayed page (i.e. year, month, day) to next one. */
  switchToNextPage(
    e?: React.SyntheticEvent<HTMLElement>,
    data?: any,
    cb?: () => void,
  ): void {}

  /** Return position numbers of cells that should be displayed as disabled */
  getDisabledPositions(): number[] {}
}

export interface ProvideHeadingValue {
  /** Return currently selected value to display in calendar header. */
  getCurrentDate(): string;
}

export class RangeSelectionPicker<
  P extends BasePickerProps,
> extends BasePicker<P> {
  public componentDidMount(): void {
    super.componentDidMount();
    const { start, end } = this.getActiveCellsPositions();
    let hoveredPos;
    if (end) {
      hoveredPos = end;
    } else if (start) {
      hoveredPos = start;
    } else {
      hoveredPos = this.getInitialDatePosition();
    }
    this.setState({ hoveredCellPosition: hoveredPos });
  }

  getActiveCellsPositions(): RangeIndexes | undefined {}

  /** Return value to display in range header. */
  getSelectedRange(): string {}
}

export class SingleSelectionPicker<
  P extends BasePickerProps,
> extends BasePicker<P> {
  public componentDidMount(): void {
    super.componentDidMount();
    const active = this.getActiveCellPosition();
    this.setState({
      hoveredCellPosition:
        isNumber(active) ? active : this.getInitialDatePosition(),
    });
  }

  getActiveCellPosition(): number | undefined {}
}
